/* eslint-disable no-restricted-syntax */

import { isPaginatedFilterColumn } from '@/hooks/Filters/constants';
import { useFilter } from '@/hooks/Filters/filter';
import usePaginatedFilterOptions from '@/hooks/Filters/usePaginatedFilterOptions';
import { OptionData } from '@hooks/Filters/interfaces';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { CHECK_ALL_KEY, checkAllOption } from './data';
import { MultiSelectionFilterProps } from './interfaces';
import Main from './Main';

const MultipleSelectionList: React.FC<MultiSelectionFilterProps> = ({
  column,
  type,
  table,
}) => {
  const [options, setOptions] = useState<OptionData[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<OptionData[]>(options);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const {
    getOptions,
    selectOption,
    clearOption,
    selectedOptions: initialValues,
  } = useFilter();

  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const isPaginated = isPaginatedFilterColumn(column);
  const [serverSearch, setServerSearch] = useState('');
  const [labels, setLabels] = useState<Record<string, string>>({});
  const {
    options: paginatedOptions,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = usePaginatedFilterOptions({
    column,
    search: serverSearch,
    enabled: isPaginated,
  });
  const currentOptions = isPaginated ? paginatedOptions : options;

  useEffect(() => {
    if (initialValues[column] && initialValues[column][type]) {
      const initialValue = initialValues[column][type];
      if (initialValue) {
        const newSelected = initialValue.selected as string[];

        setSelectedOptions(newSelected);
      }
    } else {
      setSelectedOptions([]);
    }

    if (options.length - selectedOptions.length <= 1) setIsAllChecked(true);
    if (!selectedOptions.length) setIsAllChecked(false);
  }, [initialValues, column, type, selectedOptions.length, options.length]);

  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isPaginated) return;
    async function fetchOptions() {
      const newOptions = await getOptions(column);
      setOptions(newOptions);
    }
    fetchOptions();
  }, [getOptions, column, isPaginated]);

  useEffect(() => {
    if (!isPaginated || !paginatedOptions.length) return;
    setLabels((prev) => {
      const next = { ...prev };
      let changed = false;
      paginatedOptions.forEach((option) => {
        if (next[option.id] !== option.label) {
          next[option.id] = option.label;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [isPaginated, paginatedOptions]);

  useEffect(() => {
    const filter =
      !isPaginated && search.length > 0
        ? options.filter((item) => {
            return (
              item.label.toLowerCase().includes(search.toLowerCase()) ||
              item.id === CHECK_ALL_KEY
            );
          })
        : currentOptions;
    setFilteredOptions(filter);
  }, [options, currentOptions, search, isPaginated]);

  const onSearch = useCallback(
    (newSearch: string) => {
      if (isPaginated) setServerSearch(newSearch.trim());
      else setSearch(newSearch);
    },
    [isPaginated],
  );

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) fetchNextPage();
  }, [hasNextPage, isFetching, fetchNextPage]);

  // With pagination only the loaded options are known, so "all" means every
  // loaded option is selected.
  const allChecked = isPaginated
    ? currentOptions.length > 0 &&
      currentOptions.every((option) => selectedOptions.includes(option.id))
    : isAllChecked;

  const onAdd = useCallback(
    (selectedId: string) => {
      if (type === 'multi') {
        if (selectedId === CHECK_ALL_KEY) {
          const loadedKeys = filteredOptions
            .map((option) => option.id)
            .filter((key) => key !== CHECK_ALL_KEY);
          const keys = isPaginated
            ? Array.from(new Set([...selectedOptions, ...loadedKeys]))
            : loadedKeys;
          setSelectedOptions(keys);
          setIsAllChecked(true);
          selectOption({
            table,
            column,
            type: 'multi',
            selected: keys.filter((key) => key !== CHECK_ALL_KEY),
          });
        } else {
          selectOption({
            table,
            column,
            type: 'multi',
            selected: [
              ...selectedOptions.filter((key) => key !== CHECK_ALL_KEY),
              selectedId,
            ],
          });
          setSelectedOptions([...selectedOptions, selectedId]);

          if (options.length - selectedOptions.length <= 1)
            setIsAllChecked(true);
        }
      }
      if (type === 'single') {
        if (selectedId === selectedOptions[0]) {
          setSelectedOptions([]);
        } else {
          selectOption({ table, column, type: 'single', selected: selectedId });
          setSelectedOptions([selectedId]);
        }
      }
    },
    [
      type,
      filteredOptions,
      selectOption,
      table,
      column,
      selectedOptions,
      options,
      isPaginated,
    ],
  );

  const onRemove = useCallback(
    (selectedId) => {
      let newSelectedIds = selectedOptions.filter(
        (itemId) => itemId !== selectedId,
      );
      if (newSelectedIds.length === 0) {
        setIsAllChecked(false);
        clearOption({ column, type, table });
      } else {
        if (type === 'multi') {
          if (selectedId === CHECK_ALL_KEY) {
            setSelectedOptions([]);
            setIsAllChecked(false);
            newSelectedIds = [];
            clearOption({ column, type, table });
          } else {
            selectOption({
              table,
              column,
              type: 'multi',
              selected: newSelectedIds,
            });
          }
        }
        if (type === 'single') {
          selectOption({
            table,
            column,
            type: 'single',
            selected: newSelectedIds[0],
          });
        }
      }
      setSelectedOptions(newSelectedIds);
    },
    [column, selectOption, selectedOptions, type, clearOption, table],
  );

  const selected = useMemo(() => {
    const selected: OptionData[] = isPaginated
      ? // selected ids may be absent from the current search/page results
        selectedOptions
          .filter((id) => id !== CHECK_ALL_KEY)
          .map((id) => ({
            id,
            label:
              labels[id] ??
              currentOptions.find((option) => option.id === id)?.label ??
              String(id),
          }))
      : options.filter((option) => selectedOptions.includes(option.id));
    if (allChecked) {
      return [checkAllOption, ...selected];
    }
    return selected;
  }, [
    options,
    currentOptions,
    labels,
    selectedOptions,
    allChecked,
    isPaginated,
  ]);

  const rest = useMemo(() => {
    const unselected = filteredOptions.filter(
      (option) => !selectedOptions.includes(option.id),
    );
    if (!allChecked && (!isPaginated || unselected.length > 0)) {
      return [checkAllOption, ...unselected];
    }
    return unselected;
  }, [selectedOptions, filteredOptions, allChecked, isPaginated]);

  return (
    <Main
      onAdd={onAdd}
      onRemove={onRemove}
      onSearch={onSearch}
      options={rest}
      selectedOptions={selected}
      type={type}
      isMobile={isMobile}
      isPaginated={isPaginated}
      onLoadMore={isPaginated ? onLoadMore : undefined}
    />
  );
};

export default MultipleSelectionList;
